import React, { useEffect, useState, useRef } from 'react'

// Material-UI components
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

// Constants and utility functions
import { STATUS } from '../../api/constants'
import { CONTENT, VALIDATION_RULES } from '../../constants/constants'
import { guid } from '../../utils'

// Custom components
import FormHeader from '../common/FormHeader'
import EncryptInput from '../EncryptInput'
import Form from '../common/Form'
import { CustomNoRowsOverlay } from '../common/kc-table/components'

// Base fields for the form
const BASE_FIELDS = [
  {
    name: 'key',
    label: 'Key',
    validator: [
      VALIDATION_RULES.REQUIRED,
      VALIDATION_RULES.ONLY_LETTERS_NUMBERS_DASHES_UNDERSCORE,
      VALIDATION_RULES.MUST_START_WITH_LETTER,
    ],
    title: CONTENT.general.encrypted_note,
  },
  {
    name: 'value',
    label: 'Value',
    component: EncryptInput,
    required: true,
    title: CONTENT.general.encrypted_note,
  },
  {
    name: 'comment',
    label: 'Comment',
    title: CONTENT.general.encrypted_note,
  },
]

// Default keys for the configuration
const DEFAULT_KEYS = [
  'username',
  'password',
  'client_id',
  'grant_type',
  'scope',
  'muse_oauth_url',
  'muse_dicom_url',
  'type',
  'isDev',
  'isHealthCheckEnabled',
  'client',
  'env',
  'port',
  'channelId',
  'heartBeatUrl',
  'expiredLicenseReportEnabled',
  'expiredLicenseReportBoxFolderUrl',
  'expiredLicenseReportSftpPath',
  'expiredLicenseReportUploadDestination',
]

// Grouping configuration for the fields
const GROUPING_CONFIG = {
  muse_group: [
    'muse_oauth_url',
    'muse_dicom_url',
    'username',
    'password',
    'client_id',
    'grant_type',
    'scope',
    'type',
  ],
  monitoring_group: [
    'isHealthCheckEnabled',
    'client',
    'env',
    'port',
    'channelId',
    'heartBeatUrl',
  ],
  box_folder_setting_group: ['expiredLicenseReportEnabled', 'expiredLicenseReportBoxFolderUrl', 'expiredLicenseReportSftpPath', 'expiredLicenseReportUploadDestination'],
}
const GROUP_NAMES = {
  muse_group: 'MUSE Settings',
  monitoring_group: 'Monitoring Settings',
  box_folder_setting_group: 'License Expiry Report Settings',
  others: 'Other Settings',
}

// Placeholders for the input fields
const PLACEHOLDERS = {
  muse_oauth_url: 'https://example.com/oauth',
  muse_dicom_url: 'https://example.com/dicom',
  username: 'your_username',
  password: 'your_password',
  client_id: 'your_client_id',
  grant_type: 'client_credentials',
  scope: 'read write',
  type: 'application',
  isHealthCheckEnabled: 'true/false',
  client: 'your_client_name',
  env: 'development/production',
  port: '8080',
  channelId: 'your_channel_id',
  heartBeatUrl: 'https://example.com/heartbeat',
  expiredLicenseReportEnabled: 'true/false',
  expiredLicenseReportBoxFolderUrl: 'https://box.com/folder_id',
  expiredLicenseReportSftpPath: '/path/to/sftp',
  expiredLicenseReportUploadDestination: 'box/sftp',
}

// Prepare the payload for submission
const preparePayload = (data, localRows = []) => {
  return Object.keys(data).map((id) => {
    const { key, value, encrypted, comment = '' } = data[id]
    return {
      id: localRows.includes(id) ? null : id,
      key,
      value,
      encrypted: !!encrypted,
      comment,
    }
  })
}

// Format the field data
const formatFieldData = (data) => {
  const { key, value, encrypted, comment, created_at } = data
  return {
    key,
    value,
    encrypted,
    comment: comment || '',
    created_at,
  }
}

// Get the required rows data from the data source
const getRequiredRowsData = (dataSource = {}) => {
  // facilitiyConfig is not a typo, it's the key used in backend response
  const facilityConfig = dataSource?.facilitiyConfig || []

  const newRows = facilityConfig.map((config) => config?.id)

  const formData = facilityConfig.reduce(
    (finalData, { id, ...restRowData }) => {
      finalData[id] = formatFieldData(restRowData)
      return finalData
    },
    {}
  )

  return [newRows, formData]
}

// Check if a value exists in the data list
const isValueExist = (dataList, value) =>
  dataList.find((dataValue) => dataValue === value)

const AddDefaultConfigs = ({ onClick, viewMode, variant = 'outlined' }) => (
  <Button
    variant={variant}
    onClick={onClick}
    disabled={viewMode === 'VIEW'}
    size="large"
  >
    Add Default config
  </Button>
)

// Facility configuration form component
const FacilityConfigForm = ({
  loading,
  prefillData,
  onSubmit,
  onDelete,
  viewMode,
  status,
}) => {
  const [rows, setRows] = useState([]) // State for the rows
  const [localRows, setLocalRows] = useState([]) // State for the local rows

  // Add default rows to the form
  const addDefaultRows = () => {
    const newDefaultRows = DEFAULT_KEYS.map((key) => {
      const existingRow = Object.values(initialData.current).find(
        (row) => row.key === key
      )

      if (existingRow) {
        return null // Skip adding if the key already exists
      }

      const newRowId = guid()
      return {
        id: newRowId,
        key,
        value: '',
        encrypted: false,
        comment: '',
      }
    }).filter(Boolean) // Remove null values

    setLocalRows((existingRows) => [
      ...existingRows,
      ...newDefaultRows.map((row) => row.id),
    ])

    setRows((existingRows) => [
      ...existingRows,
      ...newDefaultRows.map((row) => row.id),
    ])

    initialData.current = {
      ...initialData.current,
      ...newDefaultRows.reduce((acc, row) => {
        acc[row.id] = {
          key: row.key,
          value: row.value,
          encrypted: row.encrypted,
          comment: row.comment,
        }
        return acc
      }, {}),
    }
  }
  const initialData = useRef({})
  useEffect(() => {
    if (status === STATUS.COMPLETED) {
      setLocalRows([])
    }
  }, [status])

  // Effect to prefill the data
  useEffect(() => {
    if (prefillData?.facilitiyConfig) {
      const [newRows, newRowsData] = getRequiredRowsData(prefillData)
      initialData.current = newRowsData
      setRows(newRows)
    }
  }, [prefillData])

  // Add a new row
  const addRow = (newRowId) => {
    setLocalRows((existingRows) => [...existingRows, newRowId])
    setRows((existingRows) => [...existingRows, newRowId])
  }

  // Delete a row
  const deleteRow = async (id) => {
    const updatedRows = rows.filter((row) => row !== id)
    setRows(updatedRows)

    // avoiding api request on local state update
    if (!isValueExist(localRows, id)) {
      onDelete(id)
    }
  }

  // Handle form submission
  const handleSubmit = (formData) => {
    onSubmit(preparePayload(formData, localRows))
  }

  // Handle adding a new row
  const handleAddRow = (id) => {
    const newRowId = guid()
    addRow(newRowId)
  }

  // Handle deleting a row
  const handleDelete = (id) => {
    deleteRow(id)
  }

  // Memoized fields
  const fields = React.useMemo(() => {
    return rows.map((id) => ({
      id,
      created_at: initialData.current[id]?.created_at,
      fields: BASE_FIELDS,
    }))
  }, [rows])

  // Helper function to group fields by row
  const groupFieldsByRow = (fields, initialData) => {
    return fields.reduce((acc, field) => {
      const rowData = initialData[field.id] || {}
      const key = rowData.key || ''
      const groupName =
        Object.keys(GROUPING_CONFIG).find((group) =>
          GROUPING_CONFIG[group].some((item) => item === key)
        ) || 'others'

      if (!acc[groupName]) {
        acc[groupName] = []
      }
      // Add the field to the appropriate group
      const fieldWithPlaceholder = {
        ...field,
        fields: field.fields.map((f) => ({
          ...f,
          placeholder:
            f.name === 'key'
              ? `${key ?? 'Enter key'}`
              : f.name === 'value'
              ? `${PLACEHOLDERS[key] ?? 'Enter value'}`
              : '',
        })),
      }
      acc[groupName].push({
        ...fieldWithPlaceholder,
        heading: GROUP_NAMES[groupName] || 'Other Settings',
        key: rowData.key,
      })

      return acc
    }, {})
  }

  // Memoized row groups fields
  const rowGroupsFields = React.useMemo(() => {
    return groupFieldsByRow(fields, initialData.current)
  }, [fields])

  // Sorted fields
  const sortedFields = React.useMemo(() => {
    return fields.sort(function (a, b) {
      return a?.created_at && b?.created_at
        ? new Date(a.created_at) - new Date(b.created_at)
        : 0
    })
  }, [fields])

  return (
    <Paper
      sx={{
        padding: 3,
        marginTop: 3,
      }}
    >
      {/* Form header with title and action buttons */}
      <FormHeader marginBottom={2} {...CONTENT.facility_configs}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <AddDefaultConfigs onClick={addDefaultRows} viewMode={viewMode} />
          <Button
            variant="contained"
            onClick={handleAddRow}
            disabled={viewMode === 'VIEW'}
            size="large"
          >
            Add config
          </Button>
        </Box>
      </FormHeader>
      <Divider />
      {(sortedFields?.length === 0 ||
        Object.keys(rowGroupsFields)?.length === 0) && (
        <Box sx={{ padding: 4 }}>
          <CustomNoRowsOverlay
            text={CONTENT.facility_configs.noData}
            children={
              <AddDefaultConfigs
                onClick={handleAddRow}
                viewMode={viewMode}
                variant="contained"
              />
            }
          />
        </Box>
      )}
      {/* Form component to render the fields */}
      <Form
        fields={sortedFields || []}
        fieldGroups={rowGroupsFields || {}}
        columnCount={3}
        initialData={initialData.current}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        loading={loading}
        viewMode={viewMode}
      ></Form>
      {/* Note about encrypted fields */}
      <Typography
        variant="caption"
        textAlign="right"
        width="100%"
        display="block"
        sx={{
          marginTop: {
            xs: 2,
          },
        }}
      >
        {CONTENT.general.encrypted_note}
      </Typography>
    </Paper>
  )
}

export default FacilityConfigForm
